import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';

import 'tinymce';
import 'tinymce/themes/modern';
import 'tinymce/plugins/table';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/paste';

import { Subject } from 'rxjs/Subject';
import { FileUploadDataProvider } from '@services/files/file-upload.data.service';
import { FileUploadServiceProvider } from '@services/files/file-upload-service/file-upload.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { EmailServiceProvider } from '@services/email/email-service/email.service';
import { variablesLists } from '@models/variables-to-insert';
import { RandomStringGeneratorProvider } from '@services/helpers/random-string-generator';

declare const tinymce: any;

@Component({
	selector: 'sn-tiny-editor-web',
	template: require('./sn-tiny-editor.html'),
	styles: [require('./sn-tiny-editor.scss')]
})
export class SnTinyEditorWebComponent
	implements AfterViewInit, OnChanges, OnInit, OnDestroy {
	@Input() name: string;
	@Input() value: string; // is init value, it should set once when text editor has been init
	@Input() varsList: string;
	@Input() toolbar: string;
	@Input() height = 50;
	@Input() maxHeight = 800;
	@Output() onTextChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() onAttachmentsChange: EventEmitter<any> = new EventEmitter<any>();
	@Input() filesObj: any;
	// @Input() isQBIntegrated?: boolean;

	editor;
	toolbarName: string;
	uiContainerName: string;
	variables: any;
	isStoredFiles: boolean;
	isKeyboardVisible = false;
	uiContainer: string;
	editorSettings: any;
	private unsubscribe: Subject<void> = new Subject<void>();
	private cursorPosition;
	private lookedLinks = [
		'%contract_link%',
		'%invoice_link%',
		'%quote_link%',
		'%questionnaire_link%'
	];

	private toolbars = {
		basicToolbar:
			'bold italic underline | link | bullist numlist | alignleft aligncenter alignright alignjustify ',
		basicWithAttachments:
			'bold italic underline | bullist numlist | link attachments | alignleft aligncenter alignright alignjustify ',
		toolbarWithVariables:
			'bold italic underline | bullist numlist | link attachments | alignleft aligncenter alignright alignjustify | variables'
	};

	constructor(
		private filesDataService: FileUploadDataProvider,
		private filesService: FileUploadServiceProvider,
		private messageService: MessageService,
		private element: ElementRef,
		private emailService: EmailServiceProvider,
		private stringGenerator: RandomStringGeneratorProvider
	) {}

	private getNames() {
		this.toolbarName = `tinymce-editor-toolbar-${this.stringGenerator.getMixedString(
			4
		)}`;
		this.name = `${this.name}_${this.stringGenerator.getMixedString(6)}`;
		this.uiContainerName = `tinymce-ui-container-${this.stringGenerator.getMixedString(
			4
		)}`;
	}

	private getVariablesInFormat(editor, variables) {
		const varsInFormat = [];
		for (const item in variables) {
			const text = item;
			const value = variables[item];
			const onclick = () => {
				editor.insertContent(value);
			};
			varsInFormat.push({ text, onclick });
		}

		return varsInFormat;
	}

	private addVariables(editor, inputVarsList) {
		const defaultVarsList = 'basicMenu';
		let variablesToInsert;
		if (inputVarsList in variablesLists) {
			variablesToInsert = variablesLists[inputVarsList];
		} else {
			variablesToInsert = variablesLists[defaultVarsList];
		}

		return this.getVariablesInFormat(editor, variablesToInsert);
	}

	private setEditorContent = () => {
		this.editor.contextToolbars[0].items = 'link';
		const isValueDefined = this.value !== undefined && this.value !== null;
		if (isValueDefined) {
			this.value = this.emailService.prepareSignatureToShow(this.value);
			this.saveSelection();
			this.editor.setContent(this.value);
			this.restoreSelection(this.cursorPosition);
		} else {
			this.editor.setContent('');
		}
	};

	private getToolbar = (toolbarType = 'basicToolbar') =>
		this.toolbars[toolbarType] || null;

	private checkLinkValidation = () => {
		const fn = this.editor.windowManager.open;
		// overwrites default windowManager function
		this.editor.windowManager.open = (t, r) => {
			t.width = 660;
			if (t.title === 'Insert link') {
				const oldSubmit = t.onSubmit;
				t.body[0].label = 'Link to website or email address';
				t.body[1].label = 'Link Text';
				t.onSubmit = e => {
					const isEmail = this.isMail(e.data.href);

					if (this.isIncorrectUrl(e.data.href)) {
						if ((isEmail && this.isIncorrectMail(e.data.href)) || !isEmail) {
							this.messageService.showToaster('e_0', 'generalValidation');
							return;
						}
					}

					if (isEmail) {
						e.data.href = `mailto:${e.data.href}`;
					}

					return oldSubmit(e);
				};
				// call the original submit function
				fn.apply(this, [t, r]);
			} else {
				// call default function for other modals
				return fn(t, r);
			}
		};
	};

	private addFilesMenu = () => {
		const menu = [
			{
				text: 'From Files',
				disabled: !this.isStoredFiles,
				minWidth: 100,
				onclick: () => {
					this.showChooseFileModal();
				}
			},
			{
				text: 'From Device',
				minWidth: 100,
				onclick: () => {
					this.attachFileFromComputer();
				}
			}
		];
		return menu;
	};

	private showChooseFileModal() {
		// $('#filesModal').modal('show');
		return true;
	}

	private checkJobFiles() {
		this.filesDataService.filesObservable
			.takeUntil(this.unsubscribe)
			.subscribe((storedFiles: Array<any>) => {
				this.isStoredFiles = !!storedFiles.length;
			});
	}

	private attachFileFromComputer = (): void => {
		const input: HTMLElement = document.querySelector(
			'.attachments__input'
		) as HTMLElement;
		input.click();
	};

	private getAttachments() {
		this.filesDataService.checkedFilesObservable
			.takeUntil(this.unsubscribe)
			.subscribe((chosenFiles: Array<any>) => {
				if (this.filesObj !== undefined && chosenFiles !== null) {
					const newAttachmentsSize = this.filesService.getAllAttachmentsSize(
						this.filesObj.filesSize,
						chosenFiles
					);
					const maxSize = this.filesService.maxAttachment;
					if (newAttachmentsSize > maxSize) {
						this.messageService.showToaster('w_0', 'attachments');
						return;
					}
					this.filesObj.filesSize = newAttachmentsSize;
					chosenFiles.forEach(chosenFile => {
						let showErrorMessage = false;
						if (this.checkIfFileExist(chosenFile) === -1) {
							this.filesObj.attachments.push(chosenFile);
						} else {
							showErrorMessage = true;
						}
						if (showErrorMessage) {
							this.messageService.showToaster('e_1', 'attachments');
						}
					});
				}
			});
	}

	private checkIfFileExist(chosenFile) {
		return this.filesObj.attachments.findIndex(
			file => file.id === chosenFile.id
		);
	}

	emitNewAttachments($event) {
		this.onAttachmentsChange.emit($event);
	}

	private isIncorrectUrl(url: string): boolean {
		if (url.startsWith('http://') && url.length > 7) {
			return false;
		}

		if (url.startsWith('https://') && url.length > 8) {
			return false;
		}

		for (let i = 0, max = this.lookedLinks.length; i < max; i++) {
			if (url.includes(this.lookedLinks[i])) {
				return false;
			}
		}

		return true;
	}

	private isMail = (email: string) => email.includes('@');

	private isIncorrectMail(email) {
		const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return !emailRegex.test(email);
	}

	private saveSelection() {
		this.cursorPosition = this.editor.selection.getBookmark(2, true);
	}

	private restoreSelection(bookmark) {
		this.editor.selection.moveToBookmark(bookmark);
	}

	ngOnInit() {
		this.getNames();
		this.getAttachments();
		this.checkJobFiles();
	}

	ngOnChanges() {
		if (this.editor !== undefined) {
			this.setEditorContent();
		}
	}

	ngOnDestroy() {
		tinymce.remove(this.editor);
		this.filesDataService.setCheckedFiles(null);
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	ngAfterViewInit() {
		tinymce.init({
			anchor_bottom: false,
			anchor_top: false,
			branding: false,
			inline: true,
			fixed_toolbar_container: `#${this.toolbarName}`,
			init_instance_callback: () => {
				this.setEditorContent();
				setTimeout(() => {
					// tinymce.activeEditor.focus();
					tinymce.execCommand('mceBlur', false, `#${this.editor.id}`);
				}, 500);
			},
			selector: `#${this.name}`,
			plugins: ['link', 'lists', 'paste'],
			link_context_toolbar: true,
			ui_container: `#${this.uiContainerName}`,
			autofocus: false,
			skin_url: 'https://app.studioninja.co/build/release-pipeline-10/resources/dist/assets/skins/lightgray',
			paste_as_text: false, // paste with styles
			paste_webkit_styles: 'all',
			paste_filter_drop: false,
			paste_enable_default_filters: false,
			paste_remove_styles_if_webkit: false,
			paste_retain_style_properties: 'all',
			paste_data_images: true,
			target_list: false,
			link_title: false,
			default_link_target: '_blank',
			extended_valid_elements: 'a[href|target=_blank]',
			menubar: false,
			statusbar: false,
			toolbar: this.getToolbar(this.toolbar),
			formats: {
				alignleft: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img',
					styles: { 'text-align': 'left' }
				},
				aligncenter: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img',
					styles: { 'text-align': 'center' }
				},
				alignright: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img',
					styles: { 'text-align': 'right' }
				},
				alignfull: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img',
					styles: { 'text-align': 'justify' }
				},
				underline: { inline: 'u' }
			},
			setup: editor => {
				this.editor = editor;

				editor.on('init', () => {
					this.checkLinkValidation();
					editor.selection.select(editor.getBody(), true);
					editor.selection.collapse(false);
					// this.editor.focus();
				});

				editor.on('blur', e => {
					e.stopPropagation();
					e.stopImmediatePropagation();
					return false;
				});

				editor.on('BeforeSetContent', () => {
					this.restoreSelection(this.cursorPosition);
				});

				editor.on('ExecCommand', () => {
					this.saveSelection();
				});

				// editor.on('Focus', () => {});

				editor.on('keyup change', () => {
					const content = editor.getContent();
					this.onTextChange.emit(content);
				});

				editor.addButton('variables', {
					type: 'splitbutton',
					text: 'Insert variable',
					icon: false,
					menu: [...this.addVariables(editor, this.varsList)]
				});

				editor.addButton('attachments', {
					icon: 'fa fa-paperclip',
					menu: [...this.addFilesMenu()],
					type: 'splitbutton'
				});
			}
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=ls9lvp2s4pi!./web/modern/components/common/sn-tiny-editor/sn-tiny-editor.ts